import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getCharges, resolveApplicableEntities } from '../../services/dataService';
import { Charge, ChargeCategory } from '../../types';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useCountry } from '../../context/CountryContext';

const ChargeCard: React.FC<{ charge: Charge }> = ({ charge }) => {
    const applicableEntities = useMemo(() => resolveApplicableEntities(charge), [charge]);

    return (
        <Link to={`/charges/${charge.id}`} className="flex flex-col h-full bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 ease-in-out transform hover:-translate-y-1 border border-gray-200 p-5 group">
            <div className="flex-grow">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-shrink-0 bg-blue-50 text-primary w-12 h-12 flex items-center justify-center rounded-xl font-bold text-sm font-roboto-mono border border-blue-100">
                        {charge.code}
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </div>
                <h3 className="font-bold font-poppins text-secondary text-lg group-hover:text-primary transition-colors">{charge.name}</h3>
                <p className="text-sm text-neutral-light mt-1">{charge.fullName}</p>
                <div className="mt-3 bg-gray-50 px-3 py-1.5 rounded-md inline-block">
                    <p className="text-sm font-semibold text-primary-dark font-roboto-mono">{charge.typicalAmount}</p>
                </div>
            </div>
            {applicableEntities.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                        Applicable to: <span className="font-semibold text-gray-700">{applicableEntities[0].name}</span>
                        {applicableEntities.length > 1 && <span className="text-gray-400 ml-1">+{applicableEntities.length - 1} more</span>}
                    </p>
                </div>
            )}
        </Link>
    );
};

const ChargesIndexPage: React.FC = () => {
    const { country } = useCountry();
    const [charges, setCharges] = useState<Charge[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All Categories');

    useEffect(() => {
        getCharges(country).then(data => {
            setCharges(data);
            setLoading(false);
        });
    }, [country]);

    const categories = useMemo(() => ['All Categories', ...Object.values(ChargeCategory)], []);

    const filteredCharges = useMemo(() => {
        return charges.filter(charge => {
            const matchesSearch = (
                charge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                charge.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                charge.code.toLowerCase().includes(searchTerm.toLowerCase())
            );
            const matchesCategory = selectedCategory === 'All Categories' || charge.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [charges, searchTerm, selectedCategory]);

    return (
        <div className="bg-background py-8 sm:py-12">
            <div className="container mx-auto px-4 sm:px-6">
                <header className="text-center mb-8">
                    <h1 className="text-3xl sm:text-4xl font-extrabold font-poppins tracking-tight text-secondary sm:text-5xl">All Charges &amp; Fees</h1>
                    <p className="mt-3 text-lg text-neutral-light">Instantly find and understand any charge from banks, UPI apps, and more.</p>
                </header>

                {/* Search and Filter Section - NOT Sticky to ensure it scrolls up */}
                <div className="max-w-4xl mx-auto mb-8">
                    {/* Search Bar */}
                    <div className="relative shadow-sm rounded-lg mb-6">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <input
                            type="search"
                            className="block w-full py-3 pl-12 pr-4 text-gray-900 border border-gray-200 rounded-lg bg-white placeholder-gray-500 focus:ring-primary focus:border-primary text-base"
                            placeholder="Search charges (e.g., NFS, IMPS REV...)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Search charges"
                        />
                    </div>

                    {/* Category Filters */}
                    <div className="flex overflow-x-auto pb-2 gap-2 no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                                    selectedCategory === category
                                        ? 'bg-primary text-white border-primary shadow-md'
                                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? <LoadingSpinner /> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCharges.map(charge => (
                            <ChargeCard key={charge.id} charge={charge} />
                        ))}
                        {filteredCharges.length === 0 && (
                             <div className="col-span-full text-center py-16 px-6 bg-white rounded-lg shadow-sm border border-gray-200">
                                <h2 className="text-xl font-medium text-secondary">No Charges Found</h2>
                                <p className="text-neutral-light mt-2">
                                    {searchTerm 
                                        ? `Your search for "${searchTerm}" did not match any charges.` 
                                        : `No charges found in the "${selectedCategory}" category.`}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChargesIndexPage;